import {
  DATASHEET_LABEL,
  OUTDOOR_UNITS,
  citeDatasheet,
  datasheetCapacityPoints,
  datasheetFor,
  isUsableForSizing,
  outdoorUnitName,
  type CapacityBasis,
  type DatasheetCapacityPoint,
  type DefrostTreatment,
} from "@sts/rules-data";

/**
 * A heat pump's output at the design conditions, read from its manufacturer's
 * published capacity points — the PRE-FILL the design check offers for MIS
 * 3005-D §3.4.1(e), never a figure it uses on its own.
 *
 * INTERPOLATE, NEVER EXTRAPOLATE. The design point (outdoor design
 * temperature, design flow temperature) must lie inside a rectangle of
 * published points — or on one of their lines, or exactly on a point — all of
 * ONE basis ("max" or "nominal") and ONE defrost treatment. Bilinear
 * interpolation inside that rectangle is the only arithmetic applied, and the
 * result says which points it used. A design point outside every published
 * rectangle gets no pre-fill and says why.
 *
 * WHICH POINTS. Only those `isUsableForSizing` allows (rules-data
 * `heat-pump-datasheets.ts`): no back-up heater, known basis, defrost not
 * excluded, not marked uncertain. Groups are tried in the order
 *
 *   max + defrost included, max + defrost not stated,
 *   nominal + defrost included, nominal + defrost not stated
 *
 * — a maximum-output table is what "capacity at design conditions" means;
 * a nominal EN 14511 point is often part-load, so it is only the fallback,
 * and conservative.
 */

export interface CapacityPrefill {
  readonly catalogueId: string;
  readonly outdoorC: number;
  readonly flowC: number;
  /** Rounded to the watt. */
  readonly capacityW: number;
  readonly basis: Exclude<CapacityBasis, "unknown">;
  readonly defrost: Exclude<DefrostTreatment, "excluded">;
  /** False when the design point is itself a published point. */
  readonly interpolated: boolean;
  /** The published points the figure rests on (one, two or four). */
  readonly points: readonly DatasheetCapacityPoint[];
  /** "Interpolated between A-7/W45 (7,480 W) and A-2/W45 (7,850 W)". */
  readonly derivation: string;
  /** Every document the points came from, cited. */
  readonly citations: readonly string[];
  /** "manufacturer datasheet — confirm in the MCS Product Directory" */
  readonly label: string;
  /** What the designer should know before accepting it. */
  readonly caveats: readonly string[];
}

export type CapacityLookup =
  | { readonly found: true; readonly prefill: CapacityPrefill }
  | { readonly found: false; readonly reason: string };

const GROUPS: readonly (readonly [Exclude<CapacityBasis, "unknown">, Exclude<DefrostTreatment, "excluded">])[] = [
  ["max", "included"],
  ["max", "unknown"],
  ["nominal", "included"],
  ["nominal", "unknown"],
];

const EPS = 1e-9;

/** "A−7/W45" — outdoor air / leaving water, the datasheets' own notation (with a real minus). */
function label(o: number, f: number): string {
  return `A${o < 0 ? `−${Math.abs(o)}` : o}/W${f}`;
}

function watts(n: number): string {
  return `${Math.round(n).toLocaleString("en-GB")} W`;
}

/** The pairs (lo ≤ x ≤ hi) from `values`, tightest first; (x, x) when x is itself a value. */
function brackets(values: readonly number[], x: number): (readonly [number, number])[] {
  const sorted = [...new Set(values)].sort((a, b) => a - b);
  if (sorted.some((v) => Math.abs(v - x) < EPS)) return [[x, x]];
  const below = sorted.filter((v) => v < x).reverse();
  const above = sorted.filter((v) => v > x);
  const pairs: (readonly [number, number])[] = [];
  for (const lo of below) for (const hi of above) pairs.push([lo, hi]);
  return pairs.sort((p, q) => p[1] - p[0] - (q[1] - q[0]));
}

/**
 * The datasheet capacity of catalogue unit `catalogueId` at `outdoorC` outside
 * and `flowC` flow, or why there is none.
 */
export function datasheetCapacityAt(
  catalogueId: string | null | undefined,
  outdoorC: number,
  flowC: number,
): CapacityLookup {
  const row = datasheetFor(catalogueId);
  if (!catalogueId || !row) {
    return {
      found: false,
      reason: "No manufacturer capacity data is held for this unit — enter its output at design conditions from the MCS Product Directory or the datasheet.",
    };
  }
  const all = datasheetCapacityPoints(row);
  if (all.length === 0) {
    return {
      found: false,
      reason: `No capacity table was found for this unit (${row.capacityTableStatus}) — enter its output at design conditions from the MCS Product Directory or the datasheet.`,
    };
  }
  const usable = all.filter(isUsableForSizing);

  for (const [basis, defrost] of GROUPS) {
    const group = usable.filter((p) => p.basis === basis && p.defrost === defrost);
    if (group.length === 0) continue;
    // The lowest figure where a document prints two at one point: the one a
    // ≥100% check can stand on.
    const at = new Map<string, DatasheetCapacityPoint>();
    for (const p of group) {
      const key = `${p.outdoorC}|${p.flowC}`;
      const held = at.get(key);
      if (!held || p.capacityW < held.capacityW) at.set(key, p);
    }
    const point = (o: number, f: number) => at.get(`${o}|${f}`);
    const oPairs = brackets(group.map((p) => p.outdoorC), outdoorC);
    const fPairs = brackets(group.map((p) => p.flowC), flowC);

    // Tightest rectangle first: by the product of the two spans.
    const rects: { o: readonly [number, number]; f: readonly [number, number]; area: number }[] = [];
    for (const o of oPairs) for (const f of fPairs) rects.push({ o, f, area: (o[1] - o[0] + 1) * (f[1] - f[0] + 1) });
    rects.sort((a, b) => a.area - b.area);

    for (const { o, f } of rects) {
      const corners = [point(o[0], f[0]), point(o[0], f[1]), point(o[1], f[0]), point(o[1], f[1])];
      if (corners.some((c) => c === undefined)) continue;
      const [q00, q01, q10, q11] = corners as [
        DatasheetCapacityPoint,
        DatasheetCapacityPoint,
        DatasheetCapacityPoint,
        DatasheetCapacityPoint,
      ];
      const to = o[1] === o[0] ? 0 : (outdoorC - o[0]) / (o[1] - o[0]);
      const tf = f[1] === f[0] ? 0 : (flowC - f[0]) / (f[1] - f[0]);
      const value =
        q00.capacityW * (1 - to) * (1 - tf) +
        q01.capacityW * (1 - to) * tf +
        q10.capacityW * to * (1 - tf) +
        q11.capacityW * to * tf;
      const used = [...new Map([q00, q01, q10, q11].map((p) => [`${p.outdoorC}|${p.flowC}`, p])).values()];
      const interpolated = used.length > 1;
      const derivation = interpolated
        ? `Interpolated (straight-line, within published points only) between ${used
            .map((p) => `${label(p.outdoorC, p.flowC)} ${watts(p.capacityW)}`)
            .join(", ")}, to ${label(outdoorC, flowC)}.`
        : `Published at ${label(outdoorC, flowC)}: ${watts(value)}.`;
      const citations = [...new Set(used.map((p) => citeDatasheet(p.source)))];
      const caveats: string[] = [];
      if (basis === "nominal") {
        caveats.push(
          "An EN 14511 rating point, not the maximum-output table: for a modulating unit it is often part-load, so it may understate what the unit can deliver (conservative for the 100% check).",
        );
      }
      if (defrost === "unknown") {
        caveats.push(
          "The datasheet does not say whether this figure includes defrost. MIS 3005-D §3.4.1(f) needs the design temperatures held across defrost cycles — confirm with the manufacturer or the MCS Product Directory.",
        );
      }
      caveats.push("Excludes any back-up heater, as MIS 3005-D §3.4.1(e) requires.");
      return {
        found: true,
        prefill: {
          catalogueId,
          outdoorC,
          flowC,
          capacityW: Math.round(value),
          basis,
          defrost,
          interpolated,
          points: used,
          derivation,
          citations,
          label: DATASHEET_LABEL,
          caveats,
        },
      };
    }
  }

  const excluded = all.length - usable.length;
  return {
    found: false,
    reason:
      `The manufacturer's published points for this unit do not surround ${label(outdoorC, flowC)} ` +
      `with figures that can stand behind the 100% check` +
      (excluded > 0
        ? ` (${excluded} of ${all.length} points are left out: back-up heater included or not stated, basis unknown, defrost excluded, or marked uncertain)`
        : "") +
      ". Nothing is extrapolated — enter its output at design conditions from the MCS Product Directory or the datasheet.",
  };
}

export interface SizedCatalogueUnit {
  readonly catalogueId: string;
  readonly name: string;
  readonly prefill: CapacityPrefill;
  /** Datasheet capacity ÷ required, 1 = exactly 100%. */
  readonly fraction: number;
}

/**
 * Catalogue units whose DATASHEET output at the design conditions meets
 * `requiredW`, smallest first — the "which size?" answer, labelled as
 * datasheet figures. Units with no usable datasheet capacity at those
 * conditions are counted, not ranked.
 */
export function catalogueUnitsMeeting(
  requiredW: number,
  outdoorC: number,
  flowC: number,
): { readonly meeting: readonly SizedCatalogueUnit[]; readonly notAssessable: number; readonly tooSmall: number } {
  const meeting: SizedCatalogueUnit[] = [];
  let notAssessable = 0;
  let tooSmall = 0;
  for (const unit of OUTDOOR_UNITS) {
    const lookup = datasheetCapacityAt(unit.id, outdoorC, flowC);
    if (!lookup.found) {
      notAssessable += 1;
      continue;
    }
    const fraction = requiredW > 0 ? lookup.prefill.capacityW / requiredW : 0;
    if (fraction >= 1) {
      meeting.push({ catalogueId: unit.id, name: outdoorUnitName(unit), prefill: lookup.prefill, fraction });
    } else {
      tooSmall += 1;
    }
  }
  meeting.sort((a, b) => a.prefill.capacityW - b.prefill.capacityW);
  return { meeting, notAssessable, tooSmall };
}
